import type {
  AgeGroupWithPrice,
  DealBadge,
  DisplayPrice,
} from "@/components/ProductCard"
import type { Price, Product } from "@/queries/models"

import { getDealBadge } from "./getDealBadge"

type PriceResponse = {
  prices: Price[]
}

export type ProductWithPrices = Omit<Product, "ageGroups"> & {
  ageGroups: AgeGroupWithPrice[]
  dealBadge?: DealBadge
}

export const matchPricesWithAgeGroups = (
  products: Product[],
  priceResponse?: PriceResponse
): ProductWithPrices[] => {
  if (!priceResponse) {
    return products.map((product) => ({
      ...product,
      ageGroups: product.ageGroups.map((ageGroup) => ({
        ...ageGroup,
        price: undefined,
      })),
    }))
  }

  const priceMap = new Map(priceResponse.prices.map((p) => [p.productId, p]))

  return products.map((product) => {
    let dealBadge: DealBadge | undefined

    const matchedAgeGroups = product.ageGroups.map((ageGroup) => {
      const matchingPrice = ageGroup.productId
        ? priceMap.get(ageGroup.productId)
        : undefined

      const displayPrice: DisplayPrice | undefined = matchingPrice
        ? {
            onlinePrice: matchingPrice?.price,
            sameDayPrice: matchingPrice.sameDayPrice,
            discountedPrice: matchingPrice.discountedPrice,
            suffix: matchingPrice.currencySymbol,
            lowestPrice30Days: matchingPrice.lowestPrice30Days,
          }
        : undefined

      if (!dealBadge) {
        dealBadge = getDealBadge(matchingPrice)
      }

      return {
        ...ageGroup,
        price: displayPrice,
      }
    })

    return {
      ...product,
      ageGroups: matchedAgeGroups,
      dealBadge,
    }
  })
}
